import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import toast from 'react-hot-toast';

const ThemeContext = createContext({});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const CustomThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    // Verificar si hay un tema guardado en localStorage
    const saved = localStorage.getItem('theme');
    
    if (saved) {
      return saved === 'dark';
    }
    
    // Si no hay tema guardado, detectar el tema del sistema
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    
    // Fallback a tema oscuro si no se puede detectar
    return true;
  });

  const toggleTheme = () => {
    const newValue = !isDark;
    localStorage.setItem('theme', newValue ? 'dark' : 'light');
    setIsDark(newValue);
    
    // Mostrar notificación
    toast.success(
      `Cambiado a ${newValue ? 'tema oscuro' : 'tema claro'}`,
      {
        icon: newValue ? '🌙' : '☀️',
        duration: 2000,
      }
    );
  };

  useEffect(() => {
    // Solo ejecutar en el cliente
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Solo cambiar si no hay un tema guardado manualmente
      if (!localStorage.getItem('theme')) {
        setIsDark(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const theme = createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: {
        main: '#0066CC',
        light: '#3385d6', 
        dark: '#004080',
        contrastText: '#ffffff',
      },
      secondary: {
        main: isDark ? '#f5f5f5' : '#0f0f0f',
        light: isDark ? '#ffffff' : '#2a2a2a',
        dark: isDark ? '#cccccc' : '#000000',
        contrastText: isDark ? '#000000' : '#ffffff',
      },
      background: {
        default: isDark ? '#0a0a0a' : '#f8fafc',
        paper: isDark ? '#181818' : '#ffffff',
      },
      text: {
        primary: isDark ? '#ffffff' : '#1a202c',
        secondary: isDark ? '#b0b0b0' : '#64748b',
      },
      divider: isDark ? '#2a2a2a' : '#e2e8f0',
      action: {
        hover: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
        selected: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 700,
      },
      h2: {
        fontWeight: 600,
      },
      h3: {
        fontWeight: 600,
      },
      h4: {
        fontWeight: 600,
      },
      h5: {
        fontWeight: 600,
      },
      h6: {
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
            border: isDark ? '1px solid #2a2a2a' : 'none',
            boxShadow: isDark 
              ? '0 4px 20px rgba(0, 0, 0, 0.3)'
              : '0 2px 8px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: isDark 
                ? '0 8px 30px rgba(0, 0, 0, 0.4)'
                : '0 4px 20px rgba(0, 0, 0, 0.15)',
              transform: 'translateY(-2px)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 8,
            transition: 'all 0.2s ease-in-out',
          },
          contained: {
            background: 'linear-gradient(135deg, #0066CC, #004080)',
            '&:hover': {
              background: 'linear-gradient(135deg, #004080, #0066CC)',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0, 102, 204, 0.4)',
            },
          },
          outlined: {
            borderWidth: '2px',
            '&:hover': {
              borderWidth: '2px',
              transform: 'translateY(-1px)',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.05)',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backdropFilter: 'blur(10px)',
            backgroundColor: isDark ? 'rgba(26, 26, 26, 0.8)' : 'rgba(255, 255, 255, 0.8)',
            borderBottom: isDark ? '1px solid #2a2a2a' : '1px solid #e2e8f0',
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            transition: 'background-color 0.3s ease-in-out, color 0.3s ease-in-out',
          },
          '*': {
            transition: 'background-color 0.3s ease-in-out, border-color 0.3s ease-in-out',
          },
        },
      },
    },
  });

  const value = { isDark, toggleTheme, theme };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
